import type { Question } from "@quizzes/_schema";
import type { FunnelStep, ResponseBreakdown } from "../api";
import type { Row, Sheet } from "./xlsx";
import { buildXlsx } from "./xlsx";
import { questionLabel } from "./questions";

// The Analytics "Download report" export: a three-sheet workbook readable by
// someone who has never opened the dashboard.
//
// Sheet 1 "Read me" states what was measured, over what window, and the two
// things that mislead a reader who skips straight to the percentages: nearly
// all abandonment happens on screen one, so every later question describes the
// people who stayed; and an active cohort/persona filter silently narrows every
// number in the file.
//
// Sheet 2 "Question funnel" is one row per screen in the order it is shown.
// Sheet 3 "Answer breakdown" is one row per answer option.
//
// This replaces the flat per-answer CSV. It carries every column that CSV did
// -- including `orders` and `cvr` -- so nothing an existing consumer read went
// away; see the suppression contract below, which is load-bearing.

/** The minimum of a registry quiz this report needs. */
export type ReportQuiz = {
  slug: string;
  meta: { title: string };
  questions: Question[];
};

export type ReportArgs = {
  quiz: ReportQuiz;
  /** Inclusive ISO date bounds, as sent to the funnel route. */
  from: string;
  to: string;
  started: number;
  completed: number;
  completionRate: number | null;
  steps: FunnelStep[];
  /** Per-question responses, fetched lazily and keyed by question_id. */
  breakdowns: Record<string, ResponseBreakdown[]>;
  optionLabels: Record<string, Record<string, string>>;
  questionTypes: Record<string, string>;
  randomizedQuestionIds?: Set<string>;
  /** `facets.applied` -- echoed by the server as what it actually applied. */
  cohort?: string;
  persona?: string | null;
  /** Injected so the output is deterministic under test. */
  generatedOn: string;
};

const BAR_WIDTH = 20;
const BAR_GLYPH = "█";

/** Only these two carry a fixed option list to resolve an answer id against. */
const LABELLED_TYPES = new Set(["single", "multi"]);

function pctCell(pct: number | null | undefined) {
  // The API sends percentages as 0..100; Excel's percent format wants 0..1.
  if (pct == null) return { v: null, s: "pct1" as const };
  return { v: pct / 100, s: "pct1" as const };
}

function ratio(numerator: number, denominator: number) {
  if (!denominator) return { v: null, s: "pct1" as const };
  return { v: numerator / denominator, s: "pct1" as const };
}

function bar(pct: number | null | undefined): string {
  if (pct == null || pct <= 0) return "";
  return BAR_GLYPH.repeat(Math.max(1, Math.round((BAR_WIDTH * pct) / 100)));
}

/**
 * The answers to show for one step.
 *
 * Stale option ids -- from an option retired mid-window -- are dropped, which
 * is what the dashboard's expanded row already does. The filter applies ONLY to
 * question types that have an option list: a slider/number/ruler answer id is a
 * value, never a `value` from `options[]`, so requiring a label there would
 * silently export nothing at all for those questions.
 */
function visibleResponses(
  step: FunnelStep,
  args: ReportArgs,
): { answerId: string; label: string; r: ResponseBreakdown }[] {
  const qid = step.question_id ?? "";
  const labels = args.optionLabels[qid] ?? {};
  const labelled = LABELLED_TYPES.has(args.questionTypes[qid] ?? "");
  const out: { answerId: string; label: string; r: ResponseBreakdown }[] = [];
  for (const r of args.breakdowns[qid] ?? []) {
    const label = labels[r.answer_id];
    if (labelled && label === undefined) continue;
    out.push({ answerId: r.answer_id, label: label ?? r.answer_id, r });
  }
  return out;
}

function countDroppedResponses(args: ReportArgs): number {
  let dropped = 0;
  for (const step of args.steps) {
    const qid = step.question_id ?? "";
    if (!LABELLED_TYPES.has(args.questionTypes[qid] ?? "")) continue;
    const labels = args.optionLabels[qid] ?? {};
    for (const r of args.breakdowns[qid] ?? []) {
      if (labels[r.answer_id] === undefined) dropped += 1;
    }
  }
  return dropped;
}

function questionTextFor(step: FunnelStep, byId: Map<string, Question>): string {
  const q = step.question_id ? byId.get(step.question_id) : undefined;
  // `label` is the server's fallback (it is content-agnostic and sends the
  // question id when it has nothing better), so a screen retired from the
  // registry mid-window still names itself rather than showing a blank.
  return q ? questionLabel(q) : step.label;
}

// ------------------------------------------------------------------ Read me

function readMeSheet(args: ReportArgs, dropped: number): Sheet {
  const { started, completed } = args;
  const answerable = args.steps.filter((s) => s.breakdown_kind !== "skip");
  const first = answerable[0];
  const firstRate = first && first.viewed ? first.answered / first.viewed : null;
  const readingScreens = args.steps.filter((s) => s.breakdown_kind === "skip");
  const freeText = args.steps.filter((s) => s.breakdown_kind === "free_text");
  const randomized = args.steps.filter((s) =>
    args.randomizedQuestionIds?.has(s.question_id ?? ""),
  );
  const filtered =
    (args.cohort && args.cohort !== "all") || Boolean(args.persona);

  const entries: [string, string][] = [
    ["Quiz", `${args.quiz.meta.title}  (internal slug "${args.quiz.slug}")`],
    [
      "What this measures",
      "How everyone who landed on the quiz answered each question.",
    ],
    ["Date range", `${args.from} to ${args.to}`],
    [
      "Source",
      "Production quiz funnel telemetry, anonymous and session-level. The same data the Analytics tab shows.",
    ],
    ["Exported", args.generatedOn],
    ["", ""],
    ["Landed on the quiz", `${started.toLocaleString("en-US")} sessions`],
    [
      "Finished the quiz",
      started
        ? `${completed.toLocaleString("en-US")} sessions  (${((100 * completed) / started).toFixed(1)}% of landings)`
        : `${completed.toLocaleString("en-US")} sessions`,
    ],
  ];

  if (filtered) {
    entries.push(
      ["", ""],
      [
        "FILTER IS ACTIVE",
        "Every number in this file is filtered, not the whole quiz: " +
          [
            args.cohort && args.cohort !== "all" ? `cohort "${args.cohort}"` : "",
            args.persona ? `persona "${args.persona}"` : "",
          ]
            .filter(Boolean)
            .join(", ") +
          ". Re-export with the filters cleared for the full picture.",
      ],
    );
  }

  entries.push(
    ["", ""],
    [
      "Sheet: Question funnel",
      "One row per screen, in the order they are shown. How many men saw each screen and how many answered it.",
    ],
    [
      "Sheet: Answer breakdown",
      "One row per answer option, biggest first. The percentage is the share of the men who answered THAT question.",
    ],
    ["", ""],
    [
      "Read the % this way",
      "\"% of this question's answers\" = of the men who answered this question, the share who picked this option. " +
        "It is NOT a share of everyone who landed, because most men leave early. That share is the next column along.",
    ],
  );

  if (firstRate !== null && firstRate < 0.6) {
    entries.push([
      "The big caveat",
      `Only ${(100 * firstRate).toFixed(1)}% of the men who saw the first screen answered it, so that is where almost all ` +
        "abandonment happens. Every question after it describes the men who stayed, not the men the ads brought.",
    ]);
  }

  if (readingScreens.length) {
    entries.push([
      "Screens with no answers",
      `${readingScreens.length} screen(s) are reading screens with a Continue button, not questions. ` +
        "They appear on the funnel sheet and not on the answer sheet.",
    ]);
  }

  if (freeText.length) {
    entries.push([
      "Free-text screens",
      "Typed answers are never exported here -- they are personal data and live only in the Submissions tab.",
    ]);
  }

  if (randomized.length) {
    entries.push([
      "Randomised options",
      `${randomized.length} screen(s) show their options in a random order to each visitor, so no answer is favoured by position.`,
    ]);
  }

  if (dropped > 0) {
    entries.push([
      "Retired options",
      `${dropped} answer row(s) belong to options that are no longer in the quiz and are excluded, exactly as the dashboard excludes them. ` +
        "Percentages are the server's, measured against everyone who answered, so a question's options can total slightly under 100%.",
    ]);
  }

  entries.push([
    "Orders and Purchase CVR",
    "Quiz-attributed orders within 24h, first-party. It will not match Triple Whale, which attributes differently. " +
      "The rate is left blank where there were too few orders to state one honestly; the order count is still shown.",
  ]);

  const rows: Row[] = [
    { cells: [{ v: `${args.quiz.meta.title} - response report`, s: "title" }] },
    { cells: [] },
  ];
  for (const [key, value] of entries) {
    rows.push({
      cells: [
        { v: key, s: key ? "label" : "default" },
        { v: value, s: "wrap" },
      ],
      ...(value.length > 90 ? { height: 30 } : {}),
    });
  }

  return {
    name: "Read me",
    rows,
    columns: [30, 104],
    merges: ["A1:B1"],
    showGridLines: false,
  };
}

// ---------------------------------------------------------- Question funnel

const FUNNEL_HEADERS = [
  "#",
  "Screen",
  "Question / screen title",
  "Saw it",
  "Answered it",
  "% of landings that saw it",
  "% who answered after seeing it",
  "Exits",
  "Drop-off rate",
  "Time on page (sec)",
  "Note",
];

function funnelNote(
  step: FunnelStep,
  index: number,
  args: ReportArgs,
): string {
  const notes: string[] = [];
  if (index === 0) {
    notes.push(
      "First screen. This is where almost all abandonment happens -- read every row below it as \"of the men who got past here\".",
    );
  }
  if (step.breakdown_kind === "skip") {
    notes.push("Reading screen with a Continue button, not a question.");
  }
  if (step.breakdown_kind === "free_text") {
    notes.push("Typed answer -- not exported here, see the Submissions tab.");
  }
  if (args.randomizedQuestionIds?.has(step.question_id ?? "")) {
    notes.push("Options are shown in a random order to each visitor.");
  }
  return notes.join(" ");
}

function funnelSheet(args: ReportArgs, byId: Map<string, Question>): Sheet {
  const rows: Row[] = [
    { cells: FUNNEL_HEADERS.map((h) => ({ v: h, s: "header" as const })), height: 32 },
  ];

  args.steps.forEach((step, i) => {
    const note = funnelNote(step, i, args);
    const muted = step.breakdown_kind === "skip";
    const text = muted ? ("mutedWrap" as const) : ("text" as const);
    rows.push({
      cells: [
        { v: i + 1, s: "text" },
        { v: step.question_id ?? step.key, s: text },
        { v: questionTextFor(step, byId), s: text },
        { v: step.viewed, s: "int" },
        { v: step.answered, s: "int" },
        ratio(step.viewed, args.started),
        ratio(step.answered, step.viewed),
        { v: step.exits, s: "int" },
        pctCell(step.drop_off_pct),
        {
          v: step.time_on_page_ms == null ? null : Math.round(step.time_on_page_ms / 1000),
          s: "int",
        },
        { v: note, s: "mutedWrap" },
      ],
      ...(note ? { height: 30 } : {}),
    });
  });

  rows.push({ cells: [] });
  rows.push({
    cells: [
      null,
      null,
      { v: `Landed on the quiz: ${args.started.toLocaleString("en-US")} sessions`, s: "label" },
    ],
  });
  rows.push({
    cells: [
      null,
      null,
      {
        v: `Finished the quiz: ${args.completed.toLocaleString("en-US")} sessions`,
        s: "label",
      },
    ],
  });

  return {
    name: "Question funnel",
    rows,
    columns: [5, 22, 54, 11, 11, 14, 15, 9, 12, 13, 50],
    freeze: "D2",
    autoFilter: `A1:K${args.steps.length + 1}`,
  };
}

// --------------------------------------------------------- Answer breakdown

const ANSWER_HEADERS = [
  "#",
  "Question",
  "Answer",
  "Sessions",
  "% of this question's answers",
  "% of all landings",
  "Orders",
  "Purchase CVR",
  "share",
];

function answerSheet(args: ReportArgs, byId: Map<string, Question>): Sheet {
  const rows: Row[] = [
    { cells: ANSWER_HEADERS.map((h) => ({ v: h, s: "header" as const })), height: 32 },
  ];

  args.steps.forEach((step, i) => {
    if (step.breakdown_kind !== "options") return;
    const responses = visibleResponses(step, args);
    if (responses.length === 0) return;
    const question = questionTextFor(step, byId);
    const ordered = [...responses].sort((a, b) => b.r.count - a.r.count);

    for (const { label, r } of ordered) {
      rows.push({
        cells: [
          { v: i + 1, s: "text" },
          { v: question, s: "text" },
          { v: label, s: "text" },
          { v: r.count, s: "int" },
          pctCell(r.pct_of_answered),
          pctCell(r.pct_of_started),
          // `orders` is absent entirely on a payload from an API deployed
          // before the CVR columns existed -- the admin ships ahead of the API
          // on a merge (rule 23f). Fall back to 0 rather than write the string
          // "undefined" into a numeric cell.
          { v: r.orders ?? 0, s: "int" },
          // `cvr == null` covers BOTH the server's suppression signal (too few
          // orders to state a rate) and that same missing-key window. Either
          // way the cell is left EMPTY -- never 0, which reads as a measured
          // zero conversion rate, and never a rate this side computed. The
          // threshold is the server's alone and is deliberately not passed to
          // this module, so a client-side floor is unrepresentable here.
          r.cvr == null
            ? { v: null, s: "pct2" as const }
            : { v: r.cvr / 100, s: "pct2" as const },
          { v: bar(r.pct_of_answered), s: "bar" },
        ],
      });
    }

    rows.push({
      cells: [
        null,
        null,
        {
          v: `Answered this question: ${step.denominator_answered.toLocaleString("en-US")} sessions`,
          s: "groupRow",
        },
        ...Array(6).fill({ v: null, s: "groupRow" as const }),
      ],
    });
    rows.push({ cells: [] });
  });

  return {
    name: "Answer breakdown",
    rows,
    columns: [5, 46, 44, 11, 16, 14, 10, 13, 22],
    freeze: "D2",
    autoFilter: `A1:I${Math.max(1, rows.length - 1)}`,
  };
}

// ------------------------------------------------------------------- public

/** The three sheets, before zipping. Exported so tests can read the model. */
export function buildReportSheets(args: ReportArgs): Sheet[] {
  const byId = new Map(args.quiz.questions.map((q) => [q.id, q]));
  const dropped = countDroppedResponses(args);
  return [
    readMeSheet(args, dropped),
    funnelSheet(args, byId),
    answerSheet(args, byId),
  ];
}

export function buildResponseReport(args: ReportArgs): Blob {
  return buildXlsx(buildReportSheets(args));
}

export function reportFilename(args: Pick<ReportArgs, "quiz" | "from" | "to">): string {
  return `${args.quiz.slug}-response-report-${args.from}-to-${args.to}.xlsx`;
}
